import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import cx from "classnames";
import _ from "lodash";
import styles from "./groups.module.scss";
import {
  addAssetToGroup,
  createGroup,
  fetchGroup,
  fetchGroupChildren,
  invalidateFetchingChildrenForGroup,
  invalidateGroupChildren,
  updateGroup,
} from "../../redux/actions/groups";
import { FileService } from "../../utils/file.service";
import StreamDetailViewers from "../streams/streamDetailViewers";
import Actions from "../../components/actionbar";
import { useTransition, animated } from "@react-spring/web";
import { EditGroupMenuBar, EditGroupToolbar, RenameMenubar } from "./edit";
import ManageSource from "../../components/manageSource";
import { updateAsset, fetchAssetAnalytics } from "../../redux/actions/assets";
import { EditGroupDetailsSlider } from "./edit/groupDetails";
import { PlaceholderAssetCard } from "../hub/components/assetCard";
import EmptyMessage from "src/components/emptyMessage";
import { Group } from "src/types/groups";
import { Asset, AssetUpload } from "src/types/assets";
import Tooltip from "src/components/tooltip";
import { AddAssetsDialog } from "src/components/asset/add";
import { AddGroupSlider } from "src/components/addGroup";
import {
  getStreamSortPreference,
  getStreamViewerPreference,
  persistStreamViewerPreference,
} from "src/utils/stream.helper";
import { ReadMore } from "src/components/readMore";
import { startBackgroundUploadTask } from "src/redux/actions/backgroundTask";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";
import EventService from "src/utils/event.service";
import { GROUP } from "src/constants/adoption/sourceTypes";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";
import { Stream } from "src/types/streams";
import userRoleService from "src/utils/userRoles.service";

const emptyMessageIcon = '/images/Unauthorized_Asset.svg'
interface GroupToolBarProps {
  stream: Stream;
  sourceId: string;
  id: string;
  show: boolean;
  // handleSearchChange: (query: any) => void;
  view: string;
  setView: (viewType: string) => void;
  setEditAction: (action: string) => void;
  setOpenManageSource: (open: boolean) => void;
  toggle: boolean;
  setToggle: (toggle: boolean) => void;
}

const GroupToolBar: React.FC<GroupToolBarProps> = ({
  stream,
  sourceId,
  id,
  show,
  view,
  setView,
  setEditAction,
  setOpenManageSource,
  toggle,
  setToggle,
}) => {
  const {
    trackAddGroupClicked,
    trackAddAssetClicked,
    trackRenameClicked,
    trackManageSourceClicked,
    trackEditGroupClicked,
  } = useStreamsAdoption();

  const [transitions, api] = useTransition(show, () => ({
    initial: { opacity: 0, top: "0px" },
    from: { opacity: 1, top: "-90px" },
    enter: { opacity: 1, top: "0px" },
    leave: { opacity: 0, top: "-90px" },
  }));

  const toggleTranisitions = useTransition(toggle, {
    from: { opacity: 0, transform: "translateY(-5px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-5px)" },
    config: { duration: 200 },
  });
  const seekEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.seekEnabled", false),
  );

  useEffect(() => {
    api.start();
  }, [show]);

  useEffect(() => {
    const viewerType = getStreamViewerPreference();
    if (viewerType) {
      setView(viewerType);
    }
  }, []);

  const handleStreamViewClicked = (viewType: string) => {
    setView(viewType);
    persistStreamViewerPreference(viewType);
  };

  return transitions(
    (style, item) =>
      item && (
        <animated.div style={style} className={cx(styles.groupsMenuBar)}>
          <Actions.Container>
            <Actions.Row>
              {seekEnabled && <Actions.Seek />}
              {stream?.canEdit && (
                <Actions.IconButton
                  icon="paperflite-manage-hub"
                  tooltip="Edit"
                  onClick={() => setEditAction("editGroup")}
                />
              )}
            </Actions.Row>
            <Actions.Row>
              {stream?.canEdit && (
                <>
                  <Actions.IconButton
                    icon="paperflite-edit"
                    tooltip="Edit Group"
                    onClick={() => {
                      setEditAction("groupDetails");
                      trackEditGroupClicked({
                        streamId: sourceId || "",
                        groupId: id || "",
                      });
                    }}
                  />

                  <Actions.Dropdown
                    onMouseEnter={() => setToggle(true)}
                    onMouseLeave={() => setToggle(false)}
                  >
                    <Actions.DropdownToggle
                      icon="paperflite-add"
                      onMouseEnter={() => setToggle(true)}
                    />

                    {toggleTranisitions(
                      (style, item) =>
                        item && (
                          <animated.div
                            style={style}
                            className={styles.addEntityDropdown}
                            onMouseEnter={() => setToggle(true)}
                            onMouseLeave={() => setToggle(false)}
                          >
                            <Actions.DropdownMenuItem
                              text="Add asset"
                              onClick={() => {
                                setEditAction("add-asset");
                                trackAddAssetClicked({
                                  streamId: sourceId || "",
                                  groupId: id || "",
                                });
                              }}
                            />
                            <Actions.DropdownMenuItem
                              text="Add group"
                              onClick={() => {
                                setEditAction("add-group");
                                trackAddGroupClicked({
                                  streamId: sourceId || "",
                                  groupId: id || "",
                                });
                              }}
                            />
                          </animated.div>
                        ),
                    )}
                  </Actions.Dropdown>
                  <Actions.IconButton
                    icon="paperflite-rename"
                    tooltip="Rename"
                    onClick={() => {
                      setEditAction("rename");
                      trackRenameClicked({
                        streamId: sourceId || "",
                        sourceCta: GROUP,
                      });
                    }}
                  />
                </>
              )}
              {userRoleService.canAccessDataSource() && (
                <Actions.IconButton
                  icon="paperflite-sync"
                  tooltip="Manage Content Source"
                  onClick={() => {
                    setOpenManageSource(true);
                    trackManageSourceClicked({
                      streamId: sourceId,
                      groupId: id,
                    });
                  }}
                />
              )}

              <Actions.Set>
                <Actions.SetItem
                  icon="paperflite-categories"
                  tooltip="Card"
                  className={cx({ [styles.primaryAction]: view === "card" })}
                  onClick={() => handleStreamViewClicked("card")}
                />
                <Actions.SetItem
                  icon="paperflite-list"
                  tooltip="List"
                  className={cx({ [styles.primaryAction]: view === "list" })}
                  onClick={() => handleStreamViewClicked("list")}
                />
                <Actions.SetItem
                  icon="paperflite-column-view"
                  tooltip="Grid"
                  className={cx({ [styles.primaryAction]: view === "grid" })}
                  onClick={() => handleStreamViewClicked("grid")}
                />
              </Actions.Set>
            </Actions.Row>
          </Actions.Container>
        </animated.div>
      ),
  );
};

interface IReduxState {
  loading: boolean;
  succeeded: boolean;
  items?: any;
  item?: any;
  error: any;
}

export const useStoreState = (
  path: string,
  isRecords: boolean,
): IReduxState => {
  const reduxData = useSelector((state: any) => {
    const entityTypeData = _.get(state, `${path}`, {});
    const recordType = isRecords ? "items" : "item";

    return {
      loading: _.get(entityTypeData, "creating", false),
      succeeded: _.get(entityTypeData, "created", false),
      [recordType]: _.get(entityTypeData, `${recordType}`, {}),
      error: _.get(entityTypeData, "error", null),
    };
  }, _.isEqual);

  return reduxData;
};

const GroupView: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    trackGroupViewed,
    trackAddGroupConfirmClicked,
    trackRenameConfirmClicked,
    trackGroupAdded,
    trackAssetRenamed,
    trackGroupRenamed,
    trackGroupDetailViewed,
  } = useStreamsAdoption();

  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source");
  const sourceType = source?.split("/")[0];
  const sourceId = source?.split("/")[1];

  const [groups, setGroups] = useState<Group[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [editAction, setEditAction] = useState("");
  const [view, setView] = useState("grid");
  const [openManageSource, setOpenManageSource] = useState(false);
  const [entities, setEntities] = useState<(Asset | Group)[]>([]);
  const [invalidateSelectedEntities, setInvalidateSelectedEntities] =
    useState(false);
  const [toggle, setToggle] = useState(false);
  const [sourceStream, setSourceStream] = useState<Stream>();

  const loadingGroup = useSelector((state: any) =>
    _.get(state, `group[${id}].loading`),
  );
  const group = useSelector((state: any) =>
    _.get(state, `group[${id}].item`, null),
  );
  const fetchedGroupChildren = useSelector((state: any) =>
    _.get(state, `group[${id}].fetchedChildren`, null),
  );
  const groupChildren = useSelector((state: any) =>
    _.get(state, `group[${id}].item.children`, null),
  );
  const stream = useSelector((state: any) =>
    _.get(state, `stream.items[${sourceId}].item`, null),
  );
  const parent = useSelector((state: any) =>
    _.get(state, `group[${sourceId}].item`, null),
  );
  const errorMessage = useSelector((state: any) => _.get(state, `group[${id}].errors.errors`, null));

  const {
    loading: creatingGroup,
    succeeded: createdGroup,
    item: groupCreated,
  } = useStoreState(`group[${id}].groupCreation`, false);

  const createGroupProps: IUseFetchProps = {
    action: createGroup,
    loading: creatingGroup,
    succeeded: createdGroup,
    response: groupCreated,
    error: null,
  };

  const { handleFetch: createGroupRequest } = useFetch(createGroupProps);

  useEffect(() => {
    if (id && !group) {
      dispatch(fetchGroup({ id }));
    }

    trackGroupDetailViewed();

    return () => {
      dispatch(invalidateFetchingChildrenForGroup({id: id!}));
    }
  }, [id]);

  useEffect(() => {
    return () => {
      if (id) {
        dispatch(invalidateGroupChildren(id));
      }
    };
  }, []);

  useEffect(() => {
    if (id && group && !fetchedGroupChildren) {
       const sortPreference = getStreamSortPreference();
      dispatch(
        fetchGroupChildren({
          id,
          page: 0,
          limit: 25,
          sort: { order: sortPreference.orderBy, type: sortPreference.sortBy },
        }),
      );
      const sourceCta = EventService.getSourceCta(sourceType);
      trackGroupViewed({
        sourceCta: sourceCta,
        groupId: id || "",
        streamId: sourceStream?.id || "",
      });
    }
  }, [group]);

  useEffect(() => {
    if (fetchedGroupChildren) {
      getEntities();
    }
  }, [fetchedGroupChildren]);

  useEffect(() => {
    setAssets([]);
    setGroups([]);
    if (_.size(groupChildren) > 0) {
      getEntities();
    }
  }, [groupChildren]);

  useEffect(() => {
    assets.forEach((asset) => {
      if(!asset.analytics) {
        dispatch(
          fetchAssetAnalytics({
            streamId: sourceStream?.id || "",
            groupId: id,
            assetId: asset.id,
          }),
        );
      }
    });
  }, [assets]);

  useEffect(() => {
    if (createdGroup) {
      handleClose();
    }
  }, [createdGroup]);

  useEffect(() => {
    return () => {
      setAssets([]);
      setGroups([]);
    };
  }, []);

  useEffect(() => {
    if (_.get(stream, "id")) {
      setSourceStream(stream);
    }
  }, [stream]);

  const getEntities = () => {
    let assets = groupChildren.filter(
      (child: { entityType: string }) => child.entityType === "asset",
    );
    assets = assets.map((asset: any) => ({
      ...asset,
      type: FileService.getFileType(_.get(asset, "metadata")) || "",
    }));
    setAssets(assets);
    const groups = groupChildren.filter(
      (child: { entityType: string }) => child.entityType === "group",
    );
    setGroups(groups);
  };

  const handleSaveClick = () => {
    setEditAction("");
    const assetIds: string[] = [];
    const groupIds: string[] = [];

    entities.forEach((entity: Asset | Group) => {
      if (entity.entityType === "asset") {
        dispatch(
          updateAsset({
            id: entity.id,
            request: entity,
            streamId: sourceStream?.id || "",
            parent: id,
            type: "rename",
            event: trackAssetRenamed,
          }),
        );
        assetIds.push(entity.id);
      } else if (entity.entityType === "group") {
        const request = {
          entity: entity,
          streamId: sourceType === "streams" ? sourceStream?.id || "" : "",
          parent: id || "",
          type: "rename",
          event: trackGroupRenamed,
        };
        dispatch(updateGroup(request));
        groupIds.push(entity.id);
      }
    });

    trackRenameConfirmClicked({
      streamId: sourceStream?.id || "",
      groupIds: groupIds,
      assetIds: assetIds,
      sourceGroupId: id || "",
    });
  };

  const handleUpdateEntities = (entities: (Asset | Group)[]) => {
    setEntities(entities);
  };

  const handleSelectEntity = (entity: Asset | Group) => {
    setInvalidateSelectedEntities(false);
    if (entities.some((item) => item.id === entity.id)) {
      setEntities(entities.filter((item) => item.id !== entity.id));
    } else {
      setEntities([...entities, entity]);
    }
  };

  const handleClose = () => {
    setInvalidateSelectedEntities(true);
    setEditAction("");
    setOpenManageSource(false);
    setToggle(false);
    setEntities([]);
  };

  const handleRedirectClicked = () => {
    if (sourceType) {
      navigate(`/${sourceType}/${sourceId}`);
    } else if (_.get(group, "parent")) {
      navigate(`/groups/${group.parent.id}`);
    } else if (_.get(group, "stream.id")) {
      navigate(`/streams/${group.stream.id}`);
    } else {
      navigate(`/hub`);
    }
  };

  const handleCloseAddAsset = () => {
    setEditAction("");
  };

  const handleAddAssetFinish = (entities: any[]) => {
    if (_.size(entities) > 0) {
      const uploading = entities.filter(
        (item) => item.entityType == "asset-upload",
      );
      if (!_.isEmpty(uploading)) {
        const assest = entities.filter((item) =>
          _.includes(["asset", "asset-upload"], item.entityType),
        );
        dispatch(
          startBackgroundUploadTask({
            assets: assest as [Asset | AssetUpload],
            entity: {
              id: group.id,
              entityType: "group",
              name: group.name,
              url: `/groups/${group.id}`,
            },
          }),
        );
      } else {
        const assets = entities.filter((item) => item.entityType == "asset");
        dispatch(
          addAssetToGroup({
            groupId: id ?? "",
            assets: assets.map((item) => item.id),
          }),
        );
      }
    }

    handleCloseAddAsset();
  };

  const handleCreateGroup = (groupName: string) => {
    createGroupRequest({
      groupName: groupName,
      streamId: sourceStream?.id ?? "",
      groupId: id ?? "",
    }).then((response) => {
      trackGroupAdded({
        groupName: response.name,
        streamId: sourceStream?.id || "",
        sourceGroupId: id || "",
      });
    });
    trackAddGroupConfirmClicked({
      streamId: sourceStream?.id || "",
      group_name: groupName,
      sourceGroupId: id || "",
    });
  };

  const getParentName = () => {
    if (group && group.parent) {
      return _.get(group, "parent.name");
    } else if (sourceType === "groups") {
      return _.get(parent, "name");
    } else if (sourceType === "streams") {
      return _.get(group, "stream.name");
    } else {
      return _.get(group, "stream.name");
    }
  };

  const renderError = () => {
    if (errorMessage && errorMessage.length) {
      const error = errorMessage[0];

      if (error.code === 10002) {
        return (
          <div className={styles.emptyContainer}>
          <EmptyMessage
            width={450}
            heading="Sorry!  Some one passed you the wrong information. You were never supposed to see me :). The stream in me does not exist any longer."
            image={emptyMessageIcon}
          />
          </div>
        );
      } else if (error.code === 10007) {
        return (
          <div className={styles.emptyContainer}>
          <EmptyMessage    
            width={450}
            heading= "I’m sorry! It seems like I’m not authorised to show what you are looking for. I hate disappointing you :("
            image={emptyMessageIcon}
          />
          </div>
        );
      }
    }
    return null;
  };

  if (errorMessage) {
    return renderError();
  }

  
  return (
    <div className={styles.container}>
      {group && (
        <div className={styles.header}>
          <div className={styles.details}>
            <span className={styles.title}>
              <span className={styles.backIcon}>
                <i
                  className={cx("paperflite-left-arrow")}
                  onClick={handleRedirectClicked}
                />
              </span>
              <Tooltip placement="bottom" content={group.name}>
                <span className={styles.groupName}>{group.name}</span>
              </Tooltip>
              {getParentName() && (
                <>
                  <i
                    className={cx("paperflite-dropdown", styles.arrowIcon)}
                  ></i>
                  <Tooltip placement="bottom" content={getParentName()}>
                    <span
                      className={styles.parentName}
                      onClick={handleRedirectClicked}
                    >
                      {getParentName()}
                    </span>
                  </Tooltip>
                </>
              )}
            </span>

            {group.summary && (
              <span className={styles.description}>
                <ReadMore
                  id="read-more-content"
                  amountOfCharacters={300}
                  text={group.summary || ""}
                />
              </span>
            )}
          </div>

            <GroupToolBar
              sourceId={sourceStream?.id || ""}
              stream={group.stream}
              id={id || ""}
              view={view}
              show={editAction === ""}
              setOpenManageSource={setOpenManageSource}
              setView={setView}
              setEditAction={setEditAction}
              toggle={toggle}
              setToggle={setToggle}
            />

            {editAction === "add-asset" && (
              <AddAssetsDialog
                show={editAction == "add-asset"}
                selectableEntityTypes={["asset"]}
                source={{
                  type: "group",
                  entity: group,
                }}
                onFinish={handleAddAssetFinish}
                onClose={handleCloseAddAsset}
              />
            )}

            {editAction === "add-group" && (
              <AddGroupSlider
                creating={creatingGroup}
                handleClose={handleClose}
                handleCreateGroup={handleCreateGroup}
              />
            )}

            <EditGroupToolbar
              show={editAction === "editGroup"}
              onFinish={handleClose}
              entities={entities}
              streamId={sourceStream?.id || ""}
              groupId={id || ""}
            />

            <EditGroupMenuBar
              show={editAction === "editGroup"}
              onCancel={handleClose}
            />

            <RenameMenubar
              show={editAction === "rename"}
              onConfirm={handleSaveClick}
              onCancel={handleClose}
            />

            <EditGroupDetailsSlider
              show={editAction === "groupDetails"}
              group={group}
              streamId={_.get(sourceStream, "id", "")}
              onClose={handleClose}
            />
          </div>
      )}

      <div className={styles.contentWrapper}>
        {group &&
        !loadingGroup &&
        fetchedGroupChildren &&
        _.size(groupChildren) === 0 ? (
          <div className={styles.emptyContainer}>
            {group?.stream?.canEdit ? (
              <EmptyMessage
                width={600}
                image={"/images/streams/empty.svg"}
                heading="Nothing to see here, let’s make this group interesting..."
                message=""
                action={{
                  title: "Add asset",
                  icon: "paperflite-add",
                  onClick: () => {
                    setEditAction("add-asset");
                  },
                }}
              />
            ) : (
              <EmptyMessage
                width={600}
                image={"/images/streams/empty.svg"}
                heading="Nothing to see here, let’s make this group interesting..."
                message=""
              />
            )}
          </div>
        ) : group && !fetchedGroupChildren && _.size(groupChildren) === 0 ? (
          <div className={styles.loadingChildren}>
            {_.times(4, (i) => (
              <PlaceholderAssetCard key={i} />
            ))}
          </div>
        ) : _.size(assets) > 0 || _.size(groups) > 0 ? (
          <div className={styles.details}>
            <StreamDetailViewers
              entityType="group"
              viewer={view}
              groups={groups}
              assets={assets}
              editAction={editAction}
              handleUpdateEntities={handleUpdateEntities}
              handleSelectEntity={handleSelectEntity}
              invalidateSelectedEntities={invalidateSelectedEntities}
            />
          </div>
        ) : null}
      </div>

      {openManageSource && (
        <ManageSource
          openManageSource={openManageSource}
          group={group}
          handleClose={handleClose}
          entityType="group"
        />
      )}
    </div>
  );
};

export default GroupView;

export const useGroups = (groupId: string) => {
  return useSelector((state) => {
    let groupsState = _.get(state, `group[${groupId}]`);

    const group = _.get(groupsState, "items") || [];
    const loading = _.get(groupsState, "loadingChildren");
    const total = _.get(groupsState, "pagination.totalPages", 0);
    const hasMoreItems = _.get(groupsState, "pagination.hasMoreItems");

    let nextPage: number | null =
      _.get(groupsState, "pagination.currentPage", 0) + 1;

    if (groupsState && groupsState >= total) {
      nextPage = null;
    }

    return {
      group,
      loading,
      hasMoreItems,
      nextPage,
    };
  }, _.isEqual);
};
